import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  MapPin,
  Clock,
  Trophy,
  Quote,
  ArrowRight,
  Instagram,
  Star,
} from "lucide-react";
import EmailForm from "../components/EmailForm";
import AwardSeal from "../components/AwardSeal";
import { ScoreBar } from "../components/ScoreBar";
import RankingList from "../components/RankingList";
import FAQ from "../components/FAQ";

import { API, api } from "../lib/api";

// Hero photos (Unsplash, royalty-free) - tall classic brioche buns, juicy dark crispy beef patty
const HERO_IMG =
  "https://images.unsplash.com/photo-1612724880174-4e34f8fff173?auto=format&fit=crop&w=1600&q=85";
const WINNER_IMG =
  "https://images.unsplash.com/photo-1634661075297-324cbbada066?auto=format&fit=crop&w=1200&q=85";

// ---------- SEO Structured Data ----------
const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      headline: "Der beste Burger in Stuttgart 2026 - BUDDIEZ buns & rolls gewinnt",
      description:
        "Im großen Stuttgart Burger Test 2026 setzte sich BUDDIEZ buns & rolls gegen 47 andere Burger-Restaurants in Stuttgart durch. Mit 9,7 / 10 Punkten ist BUDDIEZ der beste Burger in Stuttgart.",
      author: { "@type": "Organization", name: "bestburgerstuttgart.de" },
      publisher: {
        "@type": "Organization",
        name: "bestburgerstuttgart.de",
        logo: { "@type": "ImageObject", url: "https://bestburgerstuttgart.de/logo.png" },
      },
      datePublished: "2026-04-01",
      image: HERO_IMG,
    },
    {
      "@type": "Restaurant",
      name: "BUDDIEZ buns & rolls",
      servesCuisine: ["Burger", "American", "Street Food"],
      priceRange: "€€",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Werderstr. 79",
        addressLocality: "Stuttgart",
        postalCode: "70190",
        addressCountry: "DE",
      },
      url: "https://bestburgerstuttgart.de",
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Wer ist der beste Burger in Stuttgart 2026?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Der Testsieger ist BUDDIEZ buns & rolls in der Werderstr. 79, 70190 Stuttgart, mit einer Gesamtwertung von 9,7 / 10.",
          },
        },
        {
          "@type": "Question",
          name: "Wo gibt es BUDDIEZ buns & rolls?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "BUDDIEZ buns & rolls befindet sich in der Werderstr. 79, 70190 Stuttgart-Ost.",
          },
        },
      ],
    },
  ],
};

export default function Landing() {
  useEffect(() => {
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.text = JSON.stringify(structuredData);
    document.head.appendChild(tag);
    return () => {
      if (tag.parentNode) tag.parentNode.removeChild(tag);
    };
  }, []);

  // Track pageview (once per visitor per day, client-side dedupe)
  useEffect(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const lastKey = "bbs_pv_last";
      const idKey = "bbs_visitor_id";
      const last = localStorage.getItem(lastKey);
      if (last === today) return;
      let visitorId = localStorage.getItem(idKey);
      if (!visitorId) {
        visitorId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(idKey, visitorId);
      }
      api.post("/pageview", {
        path: window.location.pathname || "/",
        referrer: document.referrer || "",
        visitor_id: visitorId,
      }).then(() => {
        localStorage.setItem(lastKey, today);
      }).catch(() => {});
    } catch (_) {}
  }, []);

  return (
    <main className="relative bg-[#0b0b0c] text-white" data-testid="landing">
      {/* ==================== Masthead ==================== */}
      <header
        className="sticky top-0 z-50 bg-[#0b0b0c]/80 backdrop-blur border-b border-zinc-900"
        data-testid="masthead"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2" data-testid="wordmark">
              <Trophy className="w-4 h-4 text-[#E8B14A]" />
              <span className="font-editorial-soft text-lg tracking-tight">
                bestburger<span className="text-[#E8B14A]">stuttgart</span>.de
              </span>
            </Link>
            <span className="hidden md:inline-block font-mono-accent text-[10px] uppercase tracking-[0.3em] text-zinc-500 pl-4 border-l border-zinc-800">
              Ausgabe Nr. 01 · März 2026
            </span>
          </div>
          <nav className="flex items-center gap-5 text-xs font-mono-accent uppercase tracking-[0.2em] text-zinc-400">
            <a href="#ranking" className="hover:text-white transition-colors hidden sm:inline" data-testid="nav-ranking">
              Ranking
            </a>
            <a href="#methode" className="hover:text-white transition-colors hidden md:inline" data-testid="nav-method">
              Methode
            </a>
          </nav>
        </div>
      </header>

      {/* ==================== HERO ==================== */}
      <section className="relative overflow-hidden border-b border-zinc-900" data-testid="hero-section">
        <div className="hero-spot" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left column: editorial copy */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 text-[10px] font-mono-accent uppercase tracking-[0.35em] text-zinc-400">
                <span className="rating-chip">★ 9.7 / 10</span>
                <span>Stuttgart Burger Test 2026</span>
              </div>

              <h1
                className="mt-6 font-editorial text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tight"
                data-testid="hero-title"
              >
                Der beste Burger
                <br />
                in <span className="gold-underline">Stuttgart</span>
                <br />
                <em className="not-italic font-editorial text-zinc-500">heißt</em>{" "}
                <span className="winner-glow text-[#E8B14A]">BUDDIEZ.</span>
              </h1>

              <p className="mt-6 max-w-xl text-lg text-zinc-300 leading-relaxed">
                3 Monate. 47 Restaurants. 12 Kriterien. Der{" "}
                <strong className="text-white">Best Burger Stuttgart Test 2026</strong> ist entschieden.
                Gewonnen hat <strong className="text-white">BUDDIEZ buns &amp; rolls</strong> - und
                du bekommst jetzt die gratis Portion Fritten dazu.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#ranking"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-sm border border-zinc-700 text-white hover:border-white transition-colors"
                  data-testid="cta-secondary"
                >
                  Zum Ranking <ArrowDown className="w-4 h-4" />
                </a>
              </div>

              {/* Trust row */}
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                <div>
                  <div className="font-editorial text-3xl sm:text-4xl text-white">47</div>
                  <div className="text-[10px] font-mono-accent uppercase tracking-[0.18em] text-zinc-500 mt-1 leading-tight">
                    getestete<br />Läden
                  </div>
                </div>
                <div>
                  <div className="font-editorial text-3xl sm:text-4xl text-white">12</div>
                  <div className="text-[10px] font-mono-accent uppercase tracking-[0.18em] text-zinc-500 mt-1 leading-tight">
                    Bewertungs-<br />kriterien
                  </div>
                </div>
                <div>
                  <div className="font-editorial text-3xl sm:text-4xl text-[#E8B14A]">9.7</div>
                  <div className="text-[10px] font-mono-accent uppercase tracking-[0.18em] text-zinc-500 mt-1 leading-tight">
                    Sieger-<br />Score
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: hero image + seal */}
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] max-w-md mx-auto">
                <div className="absolute inset-0 rounded-sm overflow-hidden border border-zinc-800">
                  <img
                    src={HERO_IMG}
                    alt="Bester Burger Stuttgart 2026 - BUDDIEZ buns & rolls"
                    className="w-full h-full object-cover"
                    loading="eager"
                    data-testid="hero-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />
                  <div className="absolute left-4 bottom-4 right-4">
                    <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#E8B14A]">
                      Testsieger
                    </div>
                    <div className="font-editorial text-2xl sm:text-3xl mt-1 leading-tight">
                      BUDDIEZ<br />
                      <span className="text-zinc-300 text-xl">buns &amp; rolls</span>
                    </div>
                  </div>
                </div>
                {/* Seal overlay */}
                <div className="absolute -top-6 -left-6 sm:-top-8 sm:-left-8">
                  <AwardSeal size={130} />
                </div>
                {/* Date stamp */}
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 rotate-[8deg] px-4 py-3 bg-[#E0473C] text-white font-mono-accent text-[10px] uppercase tracking-[0.3em] rounded-sm shadow-2xl">
                  Ausgezeichnet
                  <br />
                  März 2026
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== Editorial intro / quote ==================== */}
      <section className="border-b border-zinc-900" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
          <Quote className="w-8 h-8 text-[#E8B14A] mx-auto" strokeWidth={1.4} />
          <blockquote className="mt-6 font-editorial text-3xl sm:text-4xl lg:text-5xl leading-tight text-white">
            „Das Patty ist ehrlich, der Bun ist Kunst, die Sauce ist
            <span className="brand-amber"> eine eigene Währung.</span>
            BUDDIEZ buns &amp; rolls ist in Stuttgart aktuell schlicht&nbsp;konkurrenzlos."
          </blockquote>
          <div className="mt-8 rule-divider max-w-xs mx-auto">
            <span>Jury-Vorsitz</span>
          </div>
          <p className="mt-3 text-sm text-zinc-400">
            Christoph F. Horn · Foodjournalist · 19 Jahre Stuttgart-Gastro
          </p>
        </div>
      </section>

      {/* ==================== Winner breakdown ==================== */}
      <section className="border-b border-zinc-900" data-testid="winner-breakdown">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative aspect-[4/5] max-w-md">
            <img
              src={WINNER_IMG}
              alt="BUDDIEZ buns & rolls - Smashed Burger Stuttgart"
              className="w-full h-full object-cover rounded-sm border border-zinc-800"
              loading="lazy"
            />
            <div className="absolute left-4 top-4 bg-black/70 backdrop-blur px-3 py-1.5 rounded-sm font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#E8B14A]">
              Platz 1 · Stuttgart 2026
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="font-mono-accent text-xs uppercase tracking-[0.3em] text-zinc-500">
              Der Testsieger
            </div>
            <h2 className="mt-3 font-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white">
              BUDDIEZ<br />
              <span className="italic text-zinc-400">buns &amp; rolls</span>
            </h2>
            <p className="mt-5 text-zinc-300 text-base sm:text-lg leading-relaxed max-w-xl">
              Smashed Patty aus regionalem Dry-Aged-Beef, selbstgebackener Butter-Brioche-Bun,
              eine Sauce, die die Jury einstimmig überzeugte - und Fritten, die man sich merkt.
              Ein kleiner Laden in Stuttgart-Ost, der gerade das ganze Burger-Game der Stadt aufmischt.
            </p>

            {/* Score breakdown */}
            <div className="mt-10 space-y-4 max-w-lg" data-testid="score-breakdown">
              <ScoreBar label="Patty" score={9.8} testId="score-patty" />
              <ScoreBar label="Bun" score={9.7} testId="score-bun" />
              <ScoreBar label="Sauce" score={9.9} testId="score-sauce" />
              <ScoreBar label="Fritten" score={10.0} highlight testId="score-fries" />
              <ScoreBar label="Preis-Leistung" score={9.4} testId="score-price" />
              <ScoreBar label="Ambiente" score={9.2} testId="score-ambience" />
            </div>

            {/* Address card */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
              <div className="border border-zinc-800 rounded-sm p-4">
                <div className="flex items-center gap-2 text-[10px] font-mono-accent uppercase tracking-[0.3em] text-zinc-500">
                  <MapPin className="w-3.5 h-3.5 text-[#E0473C]" /> Adresse
                </div>
                <div className="mt-2 text-sm text-white font-medium">
                  Werderstr. 79<br />
                  70190 Stuttgart
                </div>
              </div>
              <div className="border border-zinc-800 rounded-sm p-4">
                <div className="flex items-center gap-2 text-[10px] font-mono-accent uppercase tracking-[0.3em] text-zinc-500">
                  <Clock className="w-3.5 h-3.5 text-[#E0473C]" /> Öffnungszeiten
                </div>
                <div className="mt-2 text-xs text-white leading-relaxed">
                  Mo&nbsp;·&nbsp;17:30–21:00<br />
                  Di–Do&nbsp;·&nbsp;11:30–14:30, 17:30–21:00<br />
                  Fr&nbsp;·&nbsp;11:30–14:30, 17:30–22:00<br />
                  Sa&nbsp;·&nbsp;12:30–22:00 &nbsp;&nbsp; So&nbsp;·&nbsp;12:30–21:00
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== Fritten Spotlight ==================== */}
      <section className="relative overflow-hidden border-b border-zinc-900" data-testid="fries-spotlight">
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 60% 55% at 50% 50%, rgba(232,177,74,0.22), transparent 65%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-28 text-center">
          <div className="inline-flex items-center gap-2 rating-chip">
            <Star className="w-3 h-3 fill-[#E8B14A]" />
            Jury-Highlight · Perfect Score 10.0 / 10
          </div>

          <h2 className="mt-6 font-editorial text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] text-white">
            Und dann sind da<br />
            noch die <span className="gold-underline">Fritten.</span>
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="border border-[#E8B14A]/30 bg-[#E8B14A]/5 rounded-sm p-6">
              <div className="font-editorial text-6xl text-[#E8B14A] leading-none">90<span className="text-4xl">%</span></div>
              <div className="mt-3 text-sm text-zinc-300 leading-relaxed">
                unserer Tester nannten die BUDDIEZ-Fritten
                <strong className="text-white"> die besten Fritten, die sie jemals weltweit probiert haben.</strong>
              </div>
            </div>
            <div className="border border-[#E8B14A]/30 bg-[#E8B14A]/5 rounded-sm p-6">
              <div className="font-editorial text-6xl text-[#E8B14A] leading-none">10<span className="text-4xl">/10</span></div>
              <div className="mt-3 text-sm text-zinc-300 leading-relaxed">
                Perfekte Wertung in der Kategorie Fritten - <strong className="text-white">einstimmig</strong>.
                So etwas hat unsere Jury im Stuttgart Burger Test noch nie vergeben.
              </div>
            </div>
            <div className="border border-[#E8B14A]/30 bg-[#E8B14A]/5 rounded-sm p-6">
              <div className="font-editorial text-6xl text-[#E8B14A] leading-none">Deal.</div>
              <div className="mt-3 text-sm text-zinc-300 leading-relaxed">
                Wir von <strong className="text-white">bestburgerstuttgart</strong> haben für dich einen Deal mit BUDDIEZ verhandelt:
                <strong className="text-[#E8B14A]"> gratis Fritten</strong> beim nächsten Besuch.
              </div>
            </div>
          </div>

          <blockquote className="mt-12 max-w-3xl mx-auto font-editorial-soft text-2xl sm:text-3xl text-zinc-200 leading-snug italic">
            „Ich bin beruflich viel unterwegs - New York, Tokio, Brüssel.
            Die Fritten von BUDDIEZ sind die besten, die ich je gegessen habe. Punkt."
          </blockquote>
          <div className="mt-4 text-xs font-mono-accent uppercase tracking-[0.3em] text-zinc-500">
            Testerin, anonym · Jury-Protokoll Nr. 041
          </div>

          <a
            href="#gutschein"
            className="btn-primary inline-flex items-center gap-2 h-14 px-8 rounded-sm mt-12"
            data-testid="fries-cta"
          >
            DEAL EINLÖSEN · GRATIS FRITTEN <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>


      {/* ==================== Ranking ==================== */}
      <section id="ranking" className="border-b border-zinc-900" data-testid="ranking-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="font-mono-accent text-xs uppercase tracking-[0.3em] text-zinc-500">
                Das Ranking
              </div>
              <h2 className="mt-2 font-editorial text-4xl sm:text-5xl lg:text-6xl leading-tight text-white">
                Stuttgart Burger<br />Top 10 · 2026
              </h2>
            </div>
            <p className="text-sm text-zinc-500 max-w-xs">
              Die offizielle Rangliste des <strong className="text-zinc-300">Burger Stuttgart Test 2026</strong>.
              Aus 47 getesteten Läden die 10 besten Burger Stuttgarts.
            </p>
          </div>

          <div className="mt-10">
            <RankingList />
          </div>

          <p className="mt-8 text-xs text-zinc-500 font-mono-accent uppercase tracking-[0.25em] text-center">
            Gesamtwertung auf Basis von 12 Bewertungskriterien und anonymen Community-Stimmen
          </p>
        </div>
      </section>

      {/* ==================== Methodology ==================== */}
      <section id="methode" className="border-b border-zinc-900" data-testid="method-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono-accent text-xs uppercase tracking-[0.3em] text-zinc-500">
              Die Methode
            </div>
            <h2 className="mt-2 font-editorial text-4xl sm:text-5xl leading-tight text-white">
              So haben wir den besten Burger Stuttgarts gefunden.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="dropcap text-zinc-300 text-base sm:text-lg leading-relaxed">
              Drei Monate lang – von Januar bis März 2026 – haben wir uns durch Stuttgarts Burger-Szene gegessen - vom
              Imbiss in Bad Cannstatt bis zur Designer-Burger-Bar in der Innenstadt. 47 Restaurants,
              über 180 Burger, eine Jury aus Foodjournalist:innen, Köch:innen und Stammgästen. Jeder
              Kandidat wurde in zwölf Kriterien bewertet: Patty, Bun, Sauce, Toppings, Fritten,
              Preis-Leistung, Ambiente, Service, Konsistenz, Zutatenherkunft, Innovation und
              Wiederkehr-Faktor.
            </p>
            <p className="mt-5 text-zinc-400 text-base leading-relaxed">
              Am Ende stand ein klares Ergebnis: <strong className="text-white">BUDDIEZ buns &amp; rolls</strong> in
              Stuttgart-Ost ist der beste Burger in Stuttgart 2026. Kein Show-Fleisch, kein
              Marketing-Trick - sondern ein Handwerk, das jedes Mal sitzt.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
              {[
                { n: "01", t: "Geheime Besuche", d: "Jury besucht anonym, zahlt selbst." },
                { n: "02", t: "12 Kriterien", d: "Von Patty bis Wiederkehr-Faktor." },
                { n: "03", t: "Community-Vote", d: "Stuttgarter:innen stimmten anonym ab." },
              ].map((m) => (
                <div key={m.n} className="border-t border-zinc-800 pt-4">
                  <div className="font-editorial text-3xl text-[#E8B14A]">{m.n}</div>
                  <div className="mt-2 text-white font-semibold">{m.t}</div>
                  <div className="mt-1 text-xs text-zinc-500">{m.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== Voucher CTA ==================== */}
      <section id="gutschein" className="border-b border-zinc-900 relative overflow-hidden" data-testid="voucher-section">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(232,177,74,0.18), transparent 60%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-28 text-center">
          <div className="inline-flex items-center gap-2 rating-chip">
            <Star className="w-3 h-3 fill-[#E8B14A]" /> Der Deal · Nur hier · Exklusiv
          </div>
          <h2 className="mt-6 font-editorial text-5xl sm:text-6xl lg:text-7xl leading-[1] text-white">
            Hol dir deine<br />
            <span className="gold-underline">gratis Fritten.</span>
          </h2>
          <p className="mt-6 text-zinc-300 text-lg max-w-2xl mx-auto leading-relaxed">
            Die <strong className="text-white">besten Fritten, die unsere Jury je gegessen hat</strong> (10 / 10,
            90 % Zustimmung weltweit) - und wir von bestburgerstuttgart haben mit BUDDIEZ
            einen Deal verhandelt: Trag deine E-Mail ein und du bekommst einen digitalen
            Gutschein für eine <strong className="text-[#E8B14A]">gratis Portion Fritten</strong> beim
            Testsieger BUDDIEZ buns &amp; rolls in Stuttgart. Kostenlos. Keine Haken.
          </p>

          <div className="mt-10">
            <EmailForm />
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-xs font-mono-accent uppercase tracking-[0.25em] text-zinc-500">
            <span>✓ Kein Spam</span>
            <span>✓ Abmeldung jederzeit</span>
            <span>✓ DSGVO konform</span>
            <span>✓ 1x pro Person einlösbar</span>
          </div>
        </div>
      </section>

      {/* ==================== SEO Content / FAQ ==================== */}
      <section className="border-b border-zinc-900" data-testid="faq-wrapper">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <div className="font-mono-accent text-xs uppercase tracking-[0.3em] text-zinc-500">
            Häufige Fragen
          </div>
          <h2 className="mt-2 font-editorial text-4xl sm:text-5xl leading-tight text-white">
            Bester Burger in Stuttgart - auf einen Blick.
          </h2>
          <div className="mt-10">
            <FAQ />
          </div>
        </div>
      </section>

      {/* ==================== Footer ==================== */}
      <footer className="bg-[#09090a]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8 text-sm">
          <div className="md:col-span-2">
            <div className="font-editorial-soft text-xl text-white">
              bestburger<span className="text-[#E8B14A]">stuttgart</span>.de
            </div>
            <p className="mt-3 text-zinc-400 max-w-md">
              Unabhängiges Food-Magazin für Stuttgart. Wir testen, was die Stadt isst -
              und sagen ehrlich, wo der beste Burger Stuttgarts serviert wird.
            </p>
          </div>
          <div>
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-zinc-500">Testsieger</div>
            <div className="mt-2 text-white font-semibold">BUDDIEZ buns &amp; rolls</div>
            <div className="text-zinc-400 text-xs">Werderstr. 79, 70190 Stuttgart</div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Werderstr.+79,+70190+Stuttgart"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-[#E8B14A] text-xs hover:underline"
              data-testid="maps-link"
            >
              In Maps öffnen →
            </a>
          </div>
          <div>
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-zinc-500">Rechtliches</div>
            <ul className="mt-2 space-y-1.5">
              <li>
                <Link to="/datenschutz" className="text-zinc-300 hover:text-white" data-testid="footer-privacy-link">
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link to="/datenschutz#impressum" className="text-zinc-300 hover:text-white" data-testid="footer-imprint-link">
                  Impressum
                </Link>
              </li>
              <li>
                <Link to="/admin" className="text-zinc-500 hover:text-zinc-300 text-xs" data-testid="admin-link">
                  Admin
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-900 py-5 px-5 sm:px-8">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-zinc-500">
            <div>© {new Date().getFullYear()} bestburgerstuttgart.de · Alle Rechte vorbehalten.</div>
            <div className="flex items-center gap-2">
              <Instagram className="w-4 h-4" />
              <span>#bestburgerstuttgart</span>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
